import { FetchModels, ViewsModels } from '../types';

export const extractReviewsPageMeta = (data: FetchModels.ReviewsResponse): ViewsModels.ReviewsPageMeta => {
  return {
    totalPages: data.meta?.total_pages ?? 0,
    reviewsPerPage: data.meta?.per_page ?? 0,
    currentPage: data.meta?.current_page ?? 0,
  };
};

export const extractTotalRating = (data: FetchModels.ReviewsResponse): ViewsModels.ReviewScoreTotal => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { rating, reviews_count } = data.product;

  if (typeof rating !== 'number') {
    throw TypeError('Failed to extract score value');
  }

  if (typeof reviews_count !== 'number') {
    throw TypeError('Failed to extract score count value');
  }

  return {
    score: rating,
    count: reviews_count,
  };
};

export const extractTotalRatingBy = (
  ratingDetail: 'price' | 'quality',
  data: FetchModels.ReviewsResponse,
): ViewsModels.ReviewScoreTotal => {
  const scoreRecord = data.product.rating_details?.find((r) => r.name.toLowerCase() === ratingDetail);
  if (scoreRecord === undefined || scoreRecord === null) {
    // Reviews added from the mobile app have only a total score
    // Refer to https://jira.lmru.tech/browse/MF-1656
    return { score: extractTotalRating(data).score };
  }

  const { value } = scoreRecord;
  if (typeof value !== 'number') {
    throw TypeError(`Failed to extract score by ${ratingDetail} value`);
  }

  return {
    score: value,
  };
};

export const extractFacetsByScore = (
  data: FetchModels.ReviewsResponse,
): ViewsModels.ReviewsByScoreFilterRecord[] => {
  const result = [1, 2, 3, 4, 5].map((score) => {
    let total = 0;
    const scoreBucket = data.facets.rating.buckets.find((b) => b.key === score);

    total = scoreBucket?.doc_count ?? 0;

    return {
      score,
      total,
    } as ViewsModels.ReviewsByScoreFilterRecord;
  });

  return result;
};

export const convertReview = (data: FetchModels.FetchedReview): ViewsModels.ReviewData => {
  const createdAt = Date.parse(data.created_at);

  return {
    reviewId: data.id,
    ratingScore: data.rating,
    isRecommended: data.recommended,
    createdAt: isNaN(createdAt) ? null : data.created_at,
    cons: data.cons,
    pros: data.pros,
    body: data.body,
    likes: data.likes,
    dislikes: data.dislikes,
    author: {
      name: data.author.name,
      initials: data.author.initials,
      avatarUrl: data.author.avatar_url,
      additionalInfo: {
        isVerified: data.verified,
        shopName: extractAuthorDetail('shop', data.author),
        frequencyOfUse: extractAuthorDetail('frequency_usage', data.author),
        durationOfUse: extractAuthorDetail('time_usage', data.author),
      },
    },
    photos: extractReviewsPhotos(data),
    comments: extractReviewsComments(data),
  };
};

export const extractReviews = (data: FetchModels.ReviewsResponse): ViewsModels.ReviewData[] => {
  let result: ViewsModels.ReviewData[] = [];
  if (data.reviews instanceof Array) {
    try {
      result = data.reviews.map(convertReview);
    } catch {
      result = [];
    }
  }
  return result;
};

export const extractAuthorDetail = (
  detailKey: 'shop' | 'time_usage' | 'frequency_usage',
  data: FetchModels.FetchedReviewAuthor,
): string | undefined => {
  return data.details?.find((r) => r.name.toLowerCase() === detailKey)?.value;
};

export const extractReviewsPhotos = (
  data: FetchModels.FetchedReview,
): ViewsModels.ReviewsPhoto[] | undefined => {
  let result;
  if (data.photos instanceof Array) {
    try {
      result = data.photos.map(convertReviewsPhoto);
    } catch {
      result = undefined;
    }
  }

  return result;
};

export const convertReviewsPhoto = (data: FetchModels.FetchedReviewsPhoto): ViewsModels.ReviewsPhoto => {
  return {
    urlOriginal: data.url_original,
    urlLarge: data.url_large,
    urlSmall: data.url_small,
    urlThumb: data.url_thumb,
    reviewId: data.owner_id,
  };
};

export const extractReviewsComments = (
  data: FetchModels.FetchedReview,
): ViewsModels.ReviewsComment[] | undefined => {
  let result;
  if (data.comments instanceof Array) {
    try {
      result = data.comments.map(convertReviewsComment);
    } catch {
      result = undefined;
    }
  }

  return result;
};

export const convertReviewsComment = (
  data: FetchModels.FetchedReviewsComment,
): ViewsModels.ReviewsComment => {
  return {
    commentId: data.id,
    body: data.body,
    author: {
      name: data.author.name,
      initials: data.author.initials,
      location: data.author.location,
      avatarUrl: data.author.avatar_url,
      occupation: data.author.occupation,
    },
  };
};

export function takePhotoId(responsePayload: FetchModels.UploadPhotoFetchResponse) {
  const photoId = responsePayload?.photo?.id;
  if (typeof photoId !== 'string' || photoId.length <= 0) {
    throw Error('Unexpected response. There is not photo ID');
  }
  return photoId;
}
